import { Button } from '@/components/ui/button';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import { PDKDeleteIcon, PDKNoUserIcon } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import getCallMeBy from '@/components/user/get-call-me-by';
import { useScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import { cn } from '@/lib/utils';
import { type AssignedTask, type User } from '@/models/registry';
import { Circle, CircleCheck } from 'lucide-react';
import { observer } from 'mobx-react';

const DealAssignedTask = observer(
  ({
    task,
    index,
    setTaskToDone,
    setTaskToIncomplete,
    handleUserAssignment,
    userOptions,
    assignedUser,
    unassignTask,
  }: {
    task: AssignedTask;
    index: number;
    setTaskToDone: (taskId: string) => void;
    setTaskToIncomplete: (taskId: string) => void;
    handleUserAssignment: (taskId: string, userId: string) => void;
    userOptions: {
      value: string;
      label: string;
      icon: React.ReactNode;
      searchTerms: string[];
    }[];
    assignedUser: User | null | undefined;
    unassignTask: (taskId: string) => void;
  }) => {
    const { shouldShowFocus, focusRef } = useScrollAndFocus({
      uiType: 'task',
      id: task.id,
    });

    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            ref={focusRef}
            key={task.id + index}
            className={cn(
              'flex flex-row gap-2 text-ellipsis text-nowrap items-center justify-between group',
              {
                'bg-blue-50 dark:bg-blue-950/30 rounded-md': shouldShowFocus,
              },
            )}
          >
            <div className="flex flex-row gap-2 items-center flex-1">
              <div>
                {task.status === 'incomplete' ? (
                  <Circle
                    className="h-3.5 w-3.5 text-gray-500 cursor-pointer"
                    onClick={() => {
                      setTaskToDone(task.id);
                    }}
                  />
                ) : (
                  <CircleCheck
                    className="h-3.5 w-3.5 text-green-500 cursor-pointer"
                    onClick={() => {
                      setTaskToIncomplete(task.id);
                    }}
                  />
                )}
              </div>
              <div className="flex-1">
                <Tooltip delayDuration={500}>
                  <TooltipTrigger>{task.task?.title}</TooltipTrigger>
                  <TooltipContent>
                    {task.task?.description || 'No description'}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="flex flex-row gap-1 items-center">
              <ComboboxPopover
                placeholder="Assign to user..."
                onChange={(userId) => handleUserAssignment(task.id, userId)}
                currentOption={userOptions.find(
                  (opt) => opt.value === (task.assignee_id || 'unassigned'),
                )}
                emptyMessage="No users found"
                options={userOptions}
                endAdornment={null}
                side="bottom"
                align="end"
              >
                <Button
                  variant="ghost"
                  className="px-1.5 py-0.5 h-fit text-xs"
                  type="button"
                >
                  {assignedUser ? (
                    <PaddockAvatar
                      src={assignedUser.avatar}
                      alt={getCallMeBy(assignedUser)}
                      fallback={`${assignedUser.first_name.charAt(0)}${assignedUser.last_name.charAt(0)}`}
                      className="w-3.5 h-3.5 rounded-full"
                    />
                  ) : (
                    <PDKNoUserIcon className="w-3.5 h-3.5 text-gray-500" />
                  )}
                </Button>
              </ComboboxPopover>
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          <ContextMenuItem
            className="flex flex-row gap-2"
            onClick={() => unassignTask(task.id)}
          >
            <PDKDeleteIcon className="h-3.5 w-3.5" />
            <span className="ellipsis">Delete Task</span>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  },
);

export default DealAssignedTask;
